/**
 * QR Code Preview Component
 * Displays a realistic QR code preview with dummy data
 */

import { useEffect, useRef } from 'react';
import QRCode from 'qrcode';

interface QRCodePreviewProps {
  width?: number;
  height?: number;
  className?: string;
  stretchHorizontal?: boolean; // Indicates width should fill container
  stretchVertical?: boolean;   // Indicates height should fill container
}

export function QRCodePreview({ width, height, className = '', stretchHorizontal, stretchVertical }: QRCodePreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const canvas = canvasRef.current;
    const container = containerRef.current;
    
    const generateQRCode = () => {
      // Dummy EPC QR-Code data for preview
      const dummyEPCData = [
        'BCD',                                    // Service Tag
        '002',                                    // Version
        '1',                                      // Character set (1 = UTF-8)
        'SCT',                                    // Identification
        'COBADEFFXXX',                           // BIC (Commerzbank example)
        'Musterfirma GmbH',                      // Beneficiary name
        'DE89370400440532013000',                // IBAN (example)
        'EUR150.00',                             // Amount with currency
        '',                                       // Purpose
        '',                                       // Structured reference
        'Rechnung RE-2024-001 - Kunde K-123',   // Unstructured reference
        ''                                        // Beneficiary to originator info
      ].join('\n');

      // For stretched QR codes, use container size
      const containerWidth = container.clientWidth || 150;
      const containerHeight = container.clientHeight || 150;
      
      console.log('QRCodePreview - props:', { width, height, stretchHorizontal, stretchVertical });
      console.log('QRCodePreview - container:', { containerWidth, containerHeight });
      
      // Determine QR code dimensions based on stretch direction
      let qrWidth: number;
      let qrHeight: number;
      
      if (stretchHorizontal && stretchVertical) {
        // Both stretched - use full container dimensions (can be rectangular!)
        qrWidth = containerWidth;
        qrHeight = containerHeight;
      } else if (stretchHorizontal && !stretchVertical) {
        // Only horizontal stretch - use container width, fixed height
        qrWidth = containerWidth;
        qrHeight = height || 150;
      } else if (!stretchHorizontal && stretchVertical) {
        // Only vertical stretch - use fixed width, container height
        qrWidth = width || 150;
        qrHeight = containerHeight;
      } else {
        // Not stretched - use provided dimensions or defaults
        qrWidth = width || 150;
        qrHeight = height || 150;
      }
      
      console.log('QRCodePreview - dimensions:', { qrWidth, qrHeight });

      // Generate QR code on canvas with specific dimensions (can be distorted!)
      QRCode.toCanvas(
        canvas,
        dummyEPCData,
        {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: qrWidth,
          // Note: qrcode library only accepts width, but we can scale the canvas
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        },
        (error) => {
          if (error) {
            console.error('QR code generation error:', error);
          } else {
            // If height is different from width, we need to scale the canvas
            if (qrHeight !== qrWidth) {
              canvas.style.width = `${qrWidth}px`;
              canvas.style.height = `${qrHeight}px`;
            }
          }
        }
      );
    };

    // Initial generation
    generateQRCode();

    // Re-generate when container size changes (for stretched QR codes)
    if (stretchHorizontal || stretchVertical) {
      const resizeObserver = new ResizeObserver(() => {
        generateQRCode();
      });
      
      resizeObserver.observe(container);
      
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [width, height, stretchHorizontal, stretchVertical]);

  return (
    <div 
      ref={containerRef}
      className={className} 
      style={{ 
        width: width ? `${width}px` : '100%', 
        height: height ? `${height}px` : '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <canvas ref={canvasRef} style={{ maxWidth: '100%', maxHeight: '100%' }} />
    </div>
  );
}
